import { useCoreServices } from '@bangle.io/context';
import { Button } from '@bangle.io/ui-components';
import { AlertTriangle } from 'lucide-react';
import React from 'react';
import { noteNameFromWsPath } from '../../common/note-save-status-copy';
import { useFailedEditorSaves } from '../../common/use-editor-save-status';

/**
 * Sticky, non-dismissible banner for any note whose durable write failed.
 * The retained Markdown stays in the save queue; this forces the failure into
 * view until every retry succeeds.
 */
export function NoteSaveFailedBanner() {
  const { editorEngine } = useCoreServices();
  const failedSaves = useFailedEditorSaves(editorEngine);

  if (failedSaves.length === 0) {
    return null;
  }

  const primary = failedSaves[0];
  if (!primary) {
    return null;
  }

  const message =
    failedSaves.length === 1
      ? t.app.editor.saveStatus.failedBanner({
          noteName: noteNameFromWsPath(primary.wsPath),
        })
      : t.app.editor.saveStatus.failedBannerGeneric;

  return (
    <div
      className="flex items-center gap-3 border-destructive/40 border-b bg-destructive px-3 py-2 text-destructive-foreground text-sm"
      data-testid="note-save-failed-banner"
      role="alert"
      aria-live="assertive"
    >
      <AlertTriangle className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1 font-medium">{message}</p>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="h-7 shrink-0"
        data-testid="note-save-failed-banner-retry"
        onClick={() => {
          editorEngine.retryFailedSave();
        }}
      >
        {t.app.editor.saveStatus.retry}
      </Button>
    </div>
  );
}
